// =====================================================================
// AccidentsLieuPlan.jsx — « Où est-ce arrivé ? » : on touche le plan (24-10-2026, demande Silviu :
// « in modulul declaratie de accident nu mai vreau sa scanam un emplacement »)
// =====================================================================
// Le plan est celui du module (image du plan d'évacuation, objets posés dessus). Le point touché est
// gardé en coordonnées NORMALISÉES 0..1, comme les objets ; l'objet le plus proche devient le texte
// proposé (« près de : Rack R12 »), que la personne peut corriger. `PointSurPlan` montre ce point
// dans la fiche, pour le registre et l'inspection.
// =====================================================================

import { useState, useEffect, useRef } from 'react';
import { safetyPlans } from '../services/api.js';
import { borne, plusProche, texteProche } from '../lib/lieuPlan.js';

const C = { line: '#e5e7eb', mute: '#7b8494', crit: '#dc2626', warnBg: '#fffbeb' };

function Point({ x, y }) {
  return (
    <div style={{
      position: 'absolute', left: `${x * 100}%`, top: `${y * 100}%`, width: 18, height: 18,
      transform: 'translate(-50%, -50%)', borderRadius: '50%', background: C.crit,
      border: '3px solid #fff', boxShadow: '0 0 0 2px rgba(220,38,38,.45)', pointerEvents: 'none',
    }} />
  );
}

export default function AccidentsLieuPlan({ lieu, onChoisir }) {
  const [plans, setPlans] = useState(null);
  const [planId, setPlanId] = useState(lieu?.planId || '');
  const [objets, setObjets] = useState([]);
  const [imageKo, setImageKo] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await safetyPlans.list();
        setPlans(p);
        setPlanId((id) => id || p[0]?.id || '');
      } catch (_) { setPlans([]); }
    })();
  }, []);

  // Les objets d'un plan ne servent qu'à CE plan : vidés au changement, et une réponse arrivée
  // après un autre changement est ignorée (sinon « près de : » nommerait un objet d'un autre plan).
  useEffect(() => {
    if (!planId) return undefined;
    let actuel = true;
    setImageKo(false);
    setObjets([]);
    (async () => {
      try { const o = await safetyPlans.assets(planId); if (actuel) setObjets(o); } catch (_) { /* sans objets : pas de texte proposé */ }
    })();
    return () => { actuel = false; };
  }, [planId]);

  const changerPlan = (id) => {
    setPlanId(id);
    if (lieu) onChoisir(null);   // le point touché était sur l'autre plan
  };

  const toucher = (e) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return;
    const x = borne((e.clientX - r.left) / r.width);
    const y = borne((e.clientY - r.top) / r.height);
    onChoisir({ planId, planX: x, planY: y, texte: texteProche(plusProche(objets, x, y)) });
  };

  if (plans === null) return <div style={{ color: C.mute, fontSize: '0.88rem' }}>Chargement du plan…</div>;
  const indisponible = (
    <div style={{ background: C.warnBg, border: '1px solid #f0dcb4', borderRadius: 6, padding: '0.6rem 0.7rem', fontSize: '0.85rem', color: '#92400E' }}>
      Le plan n’est pas disponible ici : décrivez l’endroit dans le champ ci-dessous.
    </div>
  );
  if (!plans.length) return indisponible;

  return (
    <div>
      {plans.length > 1 && (
        <select value={planId} onChange={(e) => changerPlan(e.target.value)}
          style={{ width: '100%', padding: '0.5rem', border: `1px solid ${C.line}`, borderRadius: 6, marginBottom: 8, fontSize: '0.95rem' }}>
          {plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.floor ? ` — ${p.floor}` : ''}</option>)}
        </select>
      )}
      {imageKo ? indisponible : (<>
      {/* Pas de rôle « bouton » : au clavier, le champ « Précision du lieu » fait le même travail. */}
      <div onClick={toucher} aria-label="Plan du site : touchez l’endroit de l’accident"
        style={{ position: 'relative', cursor: 'crosshair', border: `1px solid ${C.line}`, borderRadius: 8, overflow: 'hidden', touchAction: 'manipulation' }}>
        <img ref={imgRef} src={safetyPlans.imageUrl(planId)} alt="Plan du site" draggable={false}
          onError={() => setImageKo(true)} style={{ display: 'block', width: '100%', height: 'auto', userSelect: 'none' }} />
        {lieu && lieu.planId === planId && <Point x={lieu.planX} y={lieu.planY} />}
      </div>
      <div style={{ fontSize: '0.8rem', color: C.mute, marginTop: 6 }}>
        {lieu && lieu.planId === planId
          ? 'Point marqué. Touchez ailleurs pour le déplacer.'
          : 'Touchez l’endroit de l’accident sur le plan.'}
      </div>
      </>)}
    </div>
  );
}

/** Le point d'une fiche, sur son plan (lecture seule). */
export function PointSurPlan({ planId, x, y }) {
  const [ko, setKo] = useState(false);
  if (!planId || x == null || y == null || ko) return null;
  return (
    <div style={{ position: 'relative', border: `1px solid ${C.line}`, borderRadius: 8, overflow: 'hidden', maxWidth: 520, marginTop: 8 }}>
      <img src={safetyPlans.imageUrl(planId)} alt="Lieu de l’accident sur le plan" onError={() => setKo(true)}
        style={{ display: 'block', width: '100%', height: 'auto' }} />
      <Point x={x} y={y} />
    </div>
  );
}
